<template>
  <div class="lg:mt-10 md:mt-9 mt-5 lg:flex md:flex gap-2">
    <div class="container">
      <h2 class="fsize32 text4F0A4B text-left capitalize">
        <b>Common Issues</b> Around Palliative Care in India
      </h2>
      <p class="fsize16 text-dark">
        There are several common issues in palliative care in India, including: 
      </p>
      <div
        class="w-full grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-8 mt-8"
      >
        <div>
          <img
            src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/palliative-care/Lack%20of%20awareness.jpg"
            alt="lack of awareness"
            class="issues-img object-cover"
          />
          <h2
            class="fsize16 text-primary text-center mt-3 capitalize font-semibold"
          >
            Lack of awareness
          </h2>
          <p class="fsize13 mt-2 text-center">
            There is a general lack of awareness about palliative care in India,
            both among healthcare professionals and the general public. This can
            lead to delays in accessing palliative care services and a lack of
            understanding about the role and benefits of palliative care.
          </p>
        </div>
        <div>
          <img
            src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/palliative-care/Limited%20availability%20of%20palliative%20care%20services.jpg"
            alt="limited availability of palliative care services"
            class="issues-img object-cover"
          />
          <h2
            class="fsize16 text-primary text-center mt-3 capitalize font-semibold"
          >
            Limited availability of palliative care services
          </h2>
          <p class="fsize13 mt-2 text-center">
            Palliative care services are not widely available in India,
            particularly in rural areas. This can make it difficult for patients
            and families to access the care they need, and may result in
            unnecessary suffering and poor quality of life. 
          </p>
        </div>
        <div>
          <img
            src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/palliative-care/Stigma%20surrounding%20end-of-life%20care.avif"
            alt="stigma surrounding end-of-life care"
            class="issues-img object-cover"
          />
          <h2
            class="fsize16 text-primary text-center mt-3 capitalize font-semibold"
          >
            Stigma surrounding end-of-life care
          </h2>
          <p class="fsize13 mt-2 text-center">
            In many parts of India, there is a cultural taboo around discussing
            end-of-life care and death. This can make it difficult for patients
            and families to have open and honest conversations about their
            wishes and preferences for care. 
          </p>
        </div>
        <div>
          <img
            src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/palliative-care/Insufficient%20training%20of%20healthcare%20professionals.jpg"
            alt="insufficient training of healthcare professionals"
            class="issues-img object-cover"
          />
          <h2
            class="fsize16 text-primary text-center mt-3 capitalize font-semibold"
          >
            Insufficient training of healthcare professionals
          </h2>
          <p class="fsize13 mt-2 text-center">
            Many healthcare professionals in India have limited training in
            palliative care, which can result in a lack of knowledge and skills
            to provide quality care. 
          </p>
        </div>
        <div>
          <img
            src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/palliative-care/Limited%20access%20to%20pain%20medications.jpg"
            alt="limited access to pain medications"
            class="issues-img object-cover"
          />
          <h2
            class="fsize16 text-primary text-center mt-3 capitalize font-semibold"
          >
            Limited access to pain medications
          </h2>
          <p class="fsize13 mt-2 text-center">
            There is a significant gap between the need for pain relief
            medications in India and their availability. This can result in
            inadequate pain management for patients with serious illnesses.
          </p>
        </div>
        <div>
          <img
            src="https://nimbus-server-01.brained.in/nimbus-server-01/caregiversaathi/palliative-care/Financial%20barriers.jpg"
            alt="financial barriers"
            class="issues-img object-cover"
          />
          <h2
            class="fsize16 text-primary text-center mt-3 capitalize font-semibold"
          >
            Financial barriers
          </h2>
          <p class="fsize13 mt-2 text-center">
            Palliative care can be expensive, and many patients and families in
            India face financial barriers to accessing the care they need.
          </p>
        </div>
      </div>
      <p class="fsize16 font-semibold text-dark mt-8">
        Addressing these issues will require a concerted effort from healthcare
        professionals, policymakers, and the public to increase awareness about
        palliative care, improve access to services, and reduce the stigma
        surrounding end-of-life care. This will require investment in palliative
        care infrastructure, training of healthcare professionals, and the
        development of policies and regulations to support the provision of
        quality palliative care in India.
      </p>
      <div class="mt-8">
        <h2 class="fsize24 text-primary text-center capitalize">
          <b>Most Important Part</b> of Palliative Care
        </h2>
        <p class="fsize16 text-dark mt-4">
          The most important part of palliative care is to improve the quality
          of life for patients and their families who are facing serious
          illness. Palliative care is a holistic approach to care that focuses
          on relieving symptoms, managing pain, providing emotional and
          spiritual support, and helping patients and families navigate the
          complex healthcare system. 
          <br />
          In palliative care, the patient is at the center of care, and the care
          team works closely with the patient and their family to understand
          their needs, preferences, and goals of care. This involves effective
          communication, active listening, and ongoing assessment of the
          patient's physical, emotional, and spiritual needs. 
          <br />
          Palliative care also involves a team-based approach to care, with
          healthcare professionals from different disciplines working together
          to provide comprehensive care. This may include physicians, nurses,
          social workers, chaplains, and other healthcare professionals. 
          <br />
          Ultimately, the most important part of palliative care is to ensure
          that patients and their families receive care that is aligned with
          their values, preferences, and goals. This involves providing
          compassionate, patient-centered care that supports the patient's
          physical, emotional, and spiritual well-being, and helps them to
          achieve the best possible quality of life, even in the face of serious
          illness. 
        </p>
      </div>
      <div class="mt-8">
        <h2 class="fsize24 text-primary font-semibold text-center capitalize">
          Cultural nuances
        </h2>
        <p class="fsize16 text-dark mt-4">
          In cultures where end-of-life discussions are taboo, it can be
          challenging to navigate palliative care conversations. However, it's
          important to remember that the goals of palliative care are to provide
          comfort and support for patients and families, and to ensure that
          patients receive care that aligns with their wishes and values. 
          <br />
          One approach that may be helpful in these cultures is to emphasize the
          importance of planning for the future and ensuring that patients
          receive care that is aligned with their values and preferences. This
          can be framed in a way that is respectful of cultural beliefs and
          values. 
          <br />
          It may also be helpful to involve family members in discussions about
          palliative care and end-of-life care. In many cultures, family members
          play a crucial role in decision-making and may be more willing to
          discuss these issues if they feel that they are involved in the
          process. 
          <br />
          Finally, it's important to approach these conversations with
          sensitivity and respect. This may mean adapting communication styles
          and approaches to be more culturally appropriate and recognizing that
          some topics may be sensitive or difficult to discuss.  In India
          specifically, it may be helpful to work with local healthcare
          providers and community leaders to better understand cultural beliefs
          and norms around end-of-life care. Engaging with community
          organizations and religious groups may also be helpful in building
          trust and promoting open communication. 
        </p>
      </div>
    </div>
    <!-- <div class="border-left lg:w-2/12 md:w-2/12 w-full lg:mt-0 md:mt-0 mt-4">
      <SideBlogs />
    </div> -->
  </div>
</template>
<script>
import SideBlogs from "./components/SideBlogs.vue";
export default {
  components: {
    SideBlogs,
  },
};
</script>
<style>
.issues-img {
  border-radius: 20px 0px 20px 20px;
  height: 200px;
  width: 90%;
  margin: 0px auto;
}
.list-dec {
  list-style: decimal;
}
</style>
